import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Mail, Phone, DollarSign, Shield, Users, Calendar, FileText, Handshake } from 'lucide-react';
import { toast } from 'sonner';
import api, { resolveFileUrl } from '../utils/api';

const POSITION_LABELS = {
  manager: 'Manager',
  head_coach: 'Head Coach',
  recruitment: 'Recruitment',
  sporting_director: 'Sporting Director',
  academy_director: 'Academy Director',
  director_of_football: 'Director of Football',
  chief_scout: 'Chief Scout',
  ceo: 'CEO',
  chairman: 'Chairman',
  owner: 'Owner',
  agent_liaison: 'Agent Liaison',
  other: 'Other'
};

const formatPosition = (position) => {
  if (!position) return '';
  return POSITION_LABELS[position] || position.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const ClubDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchClub();
  }, [id]);

  const fetchClub = async () => {
    try {
      const response = await api.get(`/clubs/${id}`);
      setClub(response.data);
      // Fetch meetings for this club
      const meetingsRes = await api.get('/club-meetings');
      const clubMeetings = meetingsRes.data.filter(m => m.club_name === response.data.name);
      setMeetings(clubMeetings);
    } catch (error) {
      toast.error('Failed to load club details');
      navigate('/clubs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this club?')) return;

    setDeleting(true);
    try {
      await api.delete(`/clubs/${id}`);
      toast.success('Club deleted successfully');
      navigate('/clubs');
    } catch (error) {
      toast.error('Failed to delete club');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const allContacts = [];
  
  // Add primary contact if exists
  if (club.contact_person) {
    allContacts.push({
      name: club.contact_person,
      position: club.contact_position,
      email: club.contact_email,
      phone: club.contact_phone,
      isPrimary: true
    });
  }
  
  // Add additional contacts
  if (club.contacts && club.contacts.length > 0) {
    club.contacts.forEach(contact => {
      if (contact.name) {
        allContacts.push({ ...contact, isPrimary: false });
      }
    });
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/clubs"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Clubs</span>
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-6">
              {club.badge_url ? (
                <div className="w-20 h-20 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden border border-white/10 flex-shrink-0">
                  <img 
                    src={resolveFileUrl(club.badge_url)} 
                    alt={`${club.name} badge`}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ) : null}
              <div>
                <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                  {club.name}
                </h1>
                <p className="text-lg text-zinc-300">
                  {club.league} • {club.country}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Link
                to={`/clubs/${id}/edit`}
                data-testid="edit-club-button"
                className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm px-4 py-2 transition-all duration-200"
              >
                <Edit className="w-4 h-4" strokeWidth={1.5} />
                Edit
              </Link>
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-club-button"
                className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-sm px-4 py-2 transition-all duration-200 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* All Contacts */}
            <div className="glass-card p-6">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
                <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
                Club Contacts
                {allContacts.length > 0 && (
                  <span className="text-sm font-normal text-zinc-500">({allContacts.length})</span>
                )}
              </h2>
              
              {allContacts.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1} />
                  <p>No contacts added yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allContacts.map((contact, index) => (
                    <div
                      key={index}
                      data-testid={`contact-info-${index}`}
                      className={`p-4 rounded-lg border ${
                        contact.isPrimary 
                          ? 'bg-white/5 border-[#FFFFFF]/20' 
                          : 'bg-white/5 border-white/5'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-lg font-bold text-white">{contact.name}</p>
                          {contact.position && (
                            <p className="text-sm text-zinc-400">{formatPosition(contact.position)}</p>
                          )}
                        </div>
                        {contact.isPrimary && (
                          <span className="px-2 py-1 bg-white/20 text-white text-xs font-bold uppercase rounded">
                            Primary
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-2 mt-3">
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                            <a 
                              href={`mailto:${contact.email}`} 
                              className="text-sm text-zinc-300 hover:text-white transition-colors"
                            >
                              {contact.email}
                            </a>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                            <a 
                              href={`tel:${contact.phone}`} 
                              className="text-sm text-zinc-300 hover:text-white transition-colors"
                            >
                              {contact.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            {club.notes && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Notes</h3>
                <p className="text-zinc-300 leading-relaxed">{club.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Relationship Status */}
            <div className="glass-card p-6">
              <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Relationship Status</h3>
              <span
                className={`inline-block px-4 py-2 rounded text-sm font-bold uppercase ${
                  club.relationship_status === 'excellent'
                    ? 'bg-green-500/20 text-green-400'
                    : club.relationship_status === 'good'
                    ? 'bg-blue-500/20 text-blue-400'
                    : club.relationship_status === 'strained'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-zinc-500/20 text-zinc-400'
                }`}
              >
                {club.relationship_status || 'new'}
              </span>
            </div>

            {/* Transfer Budget */}
            {club.transfer_budget && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                  Transfer Budget
                </h3>
                <p className="text-3xl font-bold tabular-nums text-green-400">
                  £{(club.transfer_budget / 1000000).toFixed(2)}M
                </p>
              </div>
            )}

            {/* League Info */}
            <div className="glass-card p-6">
              <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">League Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">League</p>
                  <p className="text-lg font-bold text-white">{club.league}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Country</p>
                  <p className="text-lg font-bold text-white">{club.country}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Meeting History */}
        <div className="glass-card p-6" data-testid="club-meeting-history">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <Handshake className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
              Meeting History
            </h3>
            <Link
              to="/club-meetings"
              data-testid="view-all-meetings-link"
              className="text-sm text-white hover:underline font-bold uppercase tracking-wide"
            >
              View All Meetings
            </Link>
          </div>
          {meetings.length === 0 ? (
            <p className="text-zinc-500 text-center py-6">No meetings recorded with this club yet.</p>
          ) : (
            <div className="space-y-4">
              {meetings.map((meeting) => (
                <div key={meeting.id} className="relative pl-6 border-l-2 border-blue-500/30" data-testid={`club-meeting-${meeting.id}`}>
                  <div className="absolute left-0 top-1 w-3 h-3 bg-blue-500 rounded-full -translate-x-[7px]"></div>
                  <div className="p-4 bg-zinc-900/40 border border-white/5 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                        <span className="text-sm font-bold text-white">{formatDate(meeting.meeting_date)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-purple-400" strokeWidth={1.5} />
                        <span className="text-sm text-purple-300">{meeting.spoken_to}</span>
                      </div>
                    </div>
                    {meeting.notes && (
                      <div className="flex items-start gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-zinc-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <p className="text-sm text-zinc-400">{meeting.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubDetail;
